import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { useLocationSummary } from '../hooks/useReports';
import { exportToCSV } from '@/shared/utils/exportUtils';
import { formatDate } from '@/shared/utils/dateUtils';
import type { LocationSummaryFilters } from '../services/reportsService';

interface LocationSummarySectionProps {
  filters: LocationSummaryFilters;
}

export function LocationSummarySection({ filters }: LocationSummarySectionProps) {
  const { data, isLoading, error } = useLocationSummary(filters);

  const handleExport = () => {
    if (!data) return;

    exportToCSV(
      data.map((loc) => ({
        Location: loc.location_name,
        'Frames Added': loc.frames_added,
        'Frames Ordered': loc.frames_ordered,
        'Frames Received': loc.frames_received,
        'Stock Orders': loc.stock_orders,
        'Patient Orders': loc.patient_orders,
        'Days with Activity': loc.days_with_activity,
      })),
      'location-summary-report',
      [
        { key: 'Location', label: 'Location' },
        { key: 'Frames Added', label: 'Frames Added' },
        { key: 'Frames Ordered', label: 'Frames Ordered' },
        { key: 'Frames Received', label: 'Frames Received' },
        { key: 'Stock Orders', label: 'Stock Orders' },
        { key: 'Patient Orders', label: 'Patient Orders' },
        { key: 'Days with Activity', label: 'Days with Activity' },
      ]
    );
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Location Summary</h2>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Location Summary</h2>
        <div className="text-center py-8 text-red-600">
          Error loading data: {error.message}
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Location Summary</h2>
        <div className="text-center py-8 text-gray-500">
          No data found for the selected filters
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Location Summary</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing data from {formatDate(filters.date_from)} to {formatDate(filters.date_to)}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {data.map((location) => (
          <Card key={location.location_id} className="p-4 border-2">
            <h3 className="font-semibold text-lg mb-3">{location.location_name}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Frames Added:</span>
                <span className="font-semibold">{location.frames_added}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Frames Ordered:</span>
                <span className="font-semibold text-blue-600">
                  {location.frames_ordered}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Frames Received:</span>
                <span className="font-semibold text-green-600">
                  {location.frames_received}
                </span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Stock:</span>
                <span>{location.stock_orders}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Patient:</span>
                <span>{location.patient_orders}</span>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Days Active:</span>
                <span className="font-medium">{location.days_with_activity}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Added
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ordered
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Received
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Patient
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Days Active
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((location) => (
              <tr key={location.location_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap font-medium">
                  <span className="hidden lg:inline">{location.location_name}</span>
                  <span className="lg:hidden">{location.location_short_code || location.location_name}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {location.frames_added}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-blue-600 font-medium">
                  {location.frames_ordered}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-green-600 font-medium">
                  {location.frames_received}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {location.stock_orders}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {location.patient_orders}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                  {location.days_with_activity}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
